import { FC, useEffect, useState, useCallback } from "react";
import { GET, POST } from "../../../services/api";
import { useIntl } from "react-intl";
import { PageTitle } from "../../../../_metronic/layout/core";
import { Loading } from "../../../components/Loading/Loading";
import { BreadCrumbs } from "../../../components/BreadCrumbs/breadCrumbrs";
import io from "socket.io-client";
import useAuth from "../../../hooks/useAuth";
import styles from './index.module.css'
import { i18n } from "../../../translate/i18n";

const socket = io("https://zap-nis.ninegrid.com.br", {
//const socket = io("http://localhost:7035", {
  transports: ["websocket", "polling"],
  path: "/socket.io/",
  reconnection: true,
  reconnectionAttempts: 10,
  reconnectionDelay: 5000,
});

const WhatsAppWebContent: FC = () => {
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [loadingQr, setLoadingQr] = useState(false);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>("");
  const { loginInfo } = useAuth();

  const init = useCallback(() => {
    if (loginInfo?.pluginspaceId) {
      setStoreId(loginInfo.pluginspaceId);
    }
  }, [loginInfo]);

  useEffect(() => {
    init();

    if (!storeId) return;

    socket.emit("join", storeId);

    socket.on("qrcode", (data: { storeId: number; qrCode: string }) => {
      if (Number(data.storeId) === storeId && data.qrCode) {
        try {
          setStatusMessage("Gerando QR Code...");
          setTimeout(() => {
          // Verifica se é uma string base64 válida
          const isBase64 = /^data:image\/png;base64,/.test(data.qrCode);
          
          if (isBase64) {
            setQrCode(data.qrCode); // Usa o QR code diretamente, sem manipulação
            setLoadingQr(false);
            setStatusMessage("");
          } else {
            console.error("QR Code recebido não está no formato esperado:", data.qrCode);
          }
        }, 1000); // Pequeno delay para exibição mais suave
        } catch (error) {
          console.error("Erro ao processar QR Code:", error);
        }
      } else {
        console.warn("StoreId não corresponde ou QR Code ausente:", data);
      }
    });

    // QR Code escaneado e sessão conectada
    socket.on("session_ready", (data: { storeId: number }) => {
      if (Number(data.storeId) === storeId) {
        setQrCode(null);
        setStatusMessage("Conectando...");
        setTimeout(() => {
          setStatusMessage(`Sessão conectada para a loja ${storeId}`);
          setIsConnected(true); // Garante que a conexão foi estabelecida
          setQrCode(null); // Força a remoção do QR Code
          setTimeout(() => setStatusMessage(""), 3000);
        }, 3000);
      }
    });

    // Status da Sessão
    socket.on("mensage_status", (data: any) => {
      if (Number(data.storeId) === storeId) {
        setStatusMessage(data.message);
        setTimeout(() => setStatusMessage(""), 3000);
      }
    });

    // Desconexão
    socket.on("session_disconnect", (data: { storeId: number }): void => {
      if (Number(data.storeId) === storeId) {
        setStatusMessage("Desconectando...");
        setTimeout(() => {
          setStatusMessage(`A loja ${storeId} foi desconectada.`);
          setIsConnected(false);
          setTimeout(() => setStatusMessage(""), 3000);
        }, 3000);
      }
    });

    return () => {
      socket.off("qrcode");
      socket.off("session_ready");
      socket.off("mensage_status");
      socket.off("disconnect");
      setStoreId(null);
    };
  }, [init, storeId]);

  // Criar Sessão
  const handleCreateSession = async () => {
    if (!storeId) return;
    setQrCode(null);
    setLoadingQr(true);
    setIsConnected(false);
    setStatusMessage("Gerando QR Code...");

    try {
      await POST(`https://nis.ninegrid.com.br/api/zapnis-web/create/${storeId}`);
      //const token = localStorage.getItem('auth_token'); // ou de onde você armazena seu token
      //await POST(`http://localhost:7004/api/zapnis-web/create/${storeId}`);
    } catch (error) {
      console.error("Erro ao criar sessão:", error);
      setStatusMessage("Erro ao criar sessão.");
      setTimeout(() => setStatusMessage(""), 3000);
    }
  };

  // Consultar Status da Sessão
  const checkSessionStatus = async () => {
    if (!storeId) return;
    setStatusMessage("Verificando status...");

    try {
      const token = localStorage.getItem('auth_token');
      const response = await GET(`https://nis.ninegrid.com.br/api/zapnis-web/status/${storeId}`);
      //const response = await GET(`http://localhost:7004/api/zapnis-web/status/${storeId}`);

      if (response.success) {
        setStatusMessage(response.message.message);
        setTimeout(() => setStatusMessage(""), 3000);
      }
    } catch {
      setStatusMessage("Erro ao consultar status.");
      setTimeout(() => setStatusMessage(""), 3000);
    }
  };

  // Desconectar Sessão
  const handleDisconnectSession = async () => {
    if (!storeId) return;
    setStatusMessage("Desconectando...");

    try {
      await GET(`https://nis.ninegrid.com.br/api/zapnis-web/disconnect/${storeId}`);
      //await GET(`http://localhost:7004/api/zapnis-web/disconnect/${storeId}`);
    } catch {
      setStatusMessage("Erro ao desconectar.");
      setTimeout(() => setStatusMessage(""), 3000);
    }
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className="row g-5 gx-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-12">
            <div className="card-header py-5">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bolder fs-3 mb-1">
                  WhatsApp Web
                </span>
                <BreadCrumbs />
              </h3>
            </div>
            <div className={styles.bodyWpp}>
              <div className={styles.container}>
                <h2>{i18n.t(`whatsappweb.welcome`)}:</h2>
                <ol>
                  <li>{i18n.t(`whatsappweb.step1`)}.</li>
                  <li>{i18n.t(`whatsappweb.step2`)}.</li>
                  <li>{i18n.t(`whatsappweb.step3`)}.</li>
                  <li>{i18n.t(`whatsappweb.step4`)}.</li>
                </ol>
              </div>
              <div>
                {/* Exibição da mensagem de status */}
                {statusMessage && (
                  <div className="text-center mb-4">
                    <h3>{statusMessage}</h3>
                  </div>
                )}

                {/* Exibição do QR Code */}
                {!isConnected && qrCode && (
                  <div className="text-center mb-4">
                    <h3>Escaneie o QR Code para conectar</h3>
                    <img src={qrCode} alt="QR Code" width="256" height="256" />
                  </div>
                )}

                {/* Botões alinhados à direita */}
                <div className="d-flex justify-content-end mt-2">
                  {!isConnected && (
                    <button className="btn btn-primary me-2" onClick={handleCreateSession}>
                      Criar Sessão
                    </button>
                  )}
                  <button className="btn btn-info me-2" onClick={checkSessionStatus}>
                    Status da Sessão
                  </button>
                  {isConnected && (
                    <button className="btn btn-danger" onClick={handleDisconnectSession}>
                      Desconectar Sessão
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const WhatsAppWeb: FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const intl = useIntl();
  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: "WhatsAppWeb", path: "whatsappweb", isActive: true },
        ]}
      >
        WhatsApp Web
      </PageTitle>
      <WhatsAppWebContent />
    </>
  );
};

export { WhatsAppWeb, WhatsAppWebContent };
